import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Quote } from '../models/quote.model';
import { QuoteRevision } from '../models/quote-revision.model';
import { ExchangeRatesResponse } from '../models/exchange-rates.model';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Log } from '../models/log.model';
import { Material } from '../models/material.model';

@Injectable()
export class QuoteService {
  private http = inject(HttpClient);
  private title = inject(Title);
  private router = inject(Router);

  private quoteSubject = new BehaviorSubject<Quote | undefined>(undefined);
  public quote$ = this.quoteSubject.asObservable();
  public viewingRevisionNumber: number | undefined = undefined;
  setQuote(quote: Quote) {
    this.quoteSubject.next(quote);
  }
  get quote(): Quote {
    const quote = this.quoteSubject.getValue();
    if (!quote) {
      throw new Error('Quote not loaded');
    }
    return quote;
  }

  async getQuote(quoteId: string): Promise<Quote> {
    const quote = await firstValueFrom(
      this.http.get<Quote>(`/quotes-api/quote/${quoteId}/`),
    );
    this.quoteSubject.next(quote);
    return quote;
  }

  async patchQuote(quote: Partial<Quote>): Promise<Quote> {
    const currentQuote = this.quoteSubject.getValue();
    if (!currentQuote) {
      throw new Error('Quote must be loaded before patching');
    }
    const updatedQuote = await firstValueFrom(
      this.http.patch<Quote>(`/quotes-api/quote/${currentQuote.id}/`, quote),
    );
    this.quoteSubject.next(updatedQuote);
    return updatedQuote;
  }

  async deleteQuote(): Promise<void> {
    const quoteId = this.quote.id;
    try {
      await firstValueFrom(
        this.http.delete(`/quotes-api/quote/${quoteId}/`, {
          observe: 'response',
        }),
      );
      // Clear all quote-related state before navigating to prevent errors
      this.quoteSubject.next(undefined);
      this.revisionsSubject.next(undefined);
      this.revisionSubject.next(undefined);
      // Navigate immediately without awaiting to prevent any subsequent requests
      this.router.navigate(['/quotes']).catch((err) => {
        console.error('Navigation error:', err);
      });
    } catch (error) {
      console.error('Error deleting quote:', error);
      throw error;
    }
  }

  private revisionsSubject = new BehaviorSubject<QuoteRevision[] | undefined>(
    undefined,
  );
  public revisions$ = this.revisionsSubject
    .asObservable()
    .pipe(map((revisions) => revisions ?? []));
  get revisions(): QuoteRevision[] {
    const revisions = this.revisionsSubject.getValue();
    if (!revisions) {
      throw new Error('Revisions not loaded');
    }
    return revisions;
  }

  async getRevisions(refreshQuotes?: boolean): Promise<QuoteRevision[]> {
    const quote = this.quoteSubject.getValue();
    if (!quote) {
      throw new Error('Quote must be loaded before loading revisions');
    }
    const revisions = await firstValueFrom(
      this.http.get<QuoteRevision[]>(
        `/quotes-api/quote-revisions/${quote.id}/`,
      ),
    );
    this.revisionsSubject.next(revisions);
    if (refreshQuotes) {
      await this.getQuote(quote.id);
    }
    return revisions;
  }

  private revisionSubject = new BehaviorSubject<QuoteRevision | undefined>(
    undefined,
  );
  public revision$ = this.revisionSubject.asObservable();
  get revision(): QuoteRevision {
    const revision = this.revisionSubject.getValue();
    if (!revision) {
      throw new Error('Revision not loaded');
    }
    return revision;
  }

  async loadRevision(
    revisionNumber: number,
  ): Promise<QuoteRevision | undefined> {
    const revision = this.revisions.find(
      (rev) => rev.number === revisionNumber,
    );
    if (!revision) {
      throw new Error(`Revision number ${revisionNumber} not found`);
    }
    this.revisionSubject.next(revision);
    await this.router.navigate(['/quote', this.quote.id, revisionNumber]);
    this.title.setTitle(
      `Quote ${this.quote.number}, Rev. ${this.revision.number}`,
    );
    this.viewingRevisionNumber = revisionNumber;
    return revision;
  }

  async refreshRevision(): Promise<QuoteRevision | undefined> {
    const currentRevisionNumber = this.revisionSubject.getValue()?.number;
    await this.getRevisions(true);
    if (currentRevisionNumber == null) {
      return undefined;
    }
    const refreshedRevision = this.revisions.find(
      (revision) => revision.number === currentRevisionNumber,
    );
    if (refreshedRevision) {
      this.revisionSubject.next(refreshedRevision);
    }
    return refreshedRevision;
  }

  async publishRevision() {
    if (this.revision.published_at) {
      throw new Error('Revision is already published');
    }
    await firstValueFrom(
      this.http.patch(`/quotes-api/quote-revision/${this.revision.id}/`, {
        publish: true,
      }),
    );
    await this.getRevisions();
    await this.loadRevision(this.revision.number);
  }

  async restoreRevision(revisionId: string): Promise<QuoteRevision> {
    const restored = await firstValueFrom(
      this.http.post<QuoteRevision>(
        `/quotes-api/quote-revision/${revisionId}/restore/`,
        {},
      ),
    );
    await this.getRevisions(true);
    await this.loadRevision(restored.number);
    return restored;
  }

  async discardRevision(revisionId: string): Promise<QuoteRevision> {
    const current = await firstValueFrom(
      this.http.delete<QuoteRevision>(
        `/quotes-api/quote-revision/${revisionId}/`,
      ),
    );
    await this.getRevisions(true);
    await this.loadRevision(current.number);
    return current;
  }

  async renameRevision(
    revisionId: string,
    name: string,
  ): Promise<QuoteRevision> {
    const renamed = await firstValueFrom(
      this.http.patch<QuoteRevision>(
        `/quotes-api/quote-revision/${revisionId}/`,
        { name },
      ),
    );
    await this.getRevisions(true);
    if (this.revisionSubject.getValue()?.id === renamed.id) {
      this.revisionSubject.next(renamed);
    }
    return renamed;
  }

  async patchRevision(patch: Partial<QuoteRevision>): Promise<QuoteRevision> {
    const currentRevision = this.revisionSubject.getValue();
    if (!currentRevision) {
      throw new Error('Revision must be loaded before patching');
    }
    const updatedRevision = await firstValueFrom(
      this.http.patch<QuoteRevision>(
        `/quotes-api/quote-revision/${currentRevision.id}/`,
        patch,
      ),
    );
    // Update the revision subject
    this.revisionSubject.next(updatedRevision);
    // If a new revision was created (published revision was edited), reload everything
    if (updatedRevision.number !== currentRevision.number) {
      await this.getRevisions(true);
      await this.loadRevision(updatedRevision.number);
    }

    const rateChanged =
      patch.exchange_rate !== undefined &&
      updatedRevision.exchange_rate !== currentRevision.exchange_rate;
    const currencyChanged =
      patch.currency_code !== undefined &&
      updatedRevision.currency_code !== currentRevision.currency_code;
    const packagingOrShippingChanged =
      updatedRevision.packaging_price !== currentRevision.packaging_price ||
      updatedRevision.shipping_price !== currentRevision.shipping_price;

    if (rateChanged) {
      await this.getQuote(this.quote.id);
    } else if (packagingOrShippingChanged || currencyChanged) {
      await this.getQuote(this.quote.id);
    }
    return updatedRevision;
  }

  async getExchangeRates(
    baseCurrencyCode: string,
  ): Promise<ExchangeRatesResponse> {
    return firstValueFrom(
      this.http.get<ExchangeRatesResponse>(`/currencies-api/exchange-rates/`, {
        params: { base_currency_code: baseCurrencyCode },
      }),
    );
  }

  // Approval workflow methods

  async requestReview(): Promise<QuoteRevision> {
    const currentRevision = this.revisionSubject.getValue();
    if (!currentRevision) {
      throw new Error('Revision must be loaded before requesting review');
    }
    const updatedRevision = await firstValueFrom(
      this.http.patch<QuoteRevision>(
        `/quotes-api/quote-revision/${currentRevision.id}/`,
        { approval_action: 'request_review' },
      ),
    );
    this.revisionSubject.next(updatedRevision);
    await this.getRevisions(true);
    return updatedRevision;
  }

  async approveRevision(): Promise<QuoteRevision> {
    const currentRevision = this.revisionSubject.getValue();
    if (!currentRevision) {
      throw new Error('Revision must be loaded before approving');
    }
    const updatedRevision = await firstValueFrom(
      this.http.patch<QuoteRevision>(
        `/quotes-api/quote-revision/${currentRevision.id}/`,
        { approval_action: 'approve' },
      ),
    );
    this.revisionSubject.next(updatedRevision);
    await this.getRevisions(true);
    return updatedRevision;
  }

  async requestChanges(notes?: string): Promise<QuoteRevision> {
    const currentRevision = this.revisionSubject.getValue();
    if (!currentRevision) {
      throw new Error('Revision must be loaded before requesting changes');
    }
    const updatedRevision = await firstValueFrom(
      this.http.patch<QuoteRevision>(
        `/quotes-api/quote-revision/${currentRevision.id}/`,
        {
          approval_action: 'request_changes',
          approval_review_notes: notes || '',
        },
      ),
    );
    this.revisionSubject.next(updatedRevision);
    await this.getRevisions(true);
    return updatedRevision;
  }

  getMaterials$(): Observable<Material[]> {
    return this.http.get<Material[]>('/quotes-api/materials/');
  }

  async getQuoteStatusLabels(): Promise<string[]> {
    const settings = await firstValueFrom(
      this.http.get<{ quote_status_labels: string[] }>(
        '/settings-api/settings/',
      ),
    );
    return settings.quote_status_labels || [];
  }

  get logs$(): Observable<Log[]> {
    const quoteId = this.quote.id;
    return this.http.get<Log[]>(`/quotes-api/logs/?quoteId=${quoteId}`);
  }
}
